import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useValidation } from '../../shared/hooks/useValidation';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { popIn } from '../../shared/component/Animation';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { paperTextureModal } from '../../shared/component/paperTexture';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const FindPWModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { findPassword } = useAuth();
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const { isEmail } = useValidation();

  useEffect(() => {
    if (email.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [email]);

  if (standardModal == null) return null;

  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError('');
      return true;
    }
    if (!isEmail(value)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const changeEmailInput = (value: string) => {
    validateEmail(value);
    setEmail(value);
  }

  const handleChange = async () => {
    if (email === '') {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD')
      })
      return;
    }
    if (emailError) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT')
      })
      return;
    }

    await findPassword({ email });
  };

  return (
    <FindModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ChangeModalComp>
        <Title>{getModalMessage('FIND_PASSWORD_TITLE')}</Title>
        <InputWrapper>
          <HeadlessInput
            type={'text'}
            value={email}
            handleChange={(e) => changeEmailInput(e.target.value)}
            placeholder={getModalMessage('ENTER_EMAIL')}
            StyledInput={MemberInfoValue}
          />
          <ErrorText>{emailError !== '' ? emailError : ''}</ErrorText>
        </InputWrapper>
        <BtnContainer>
          <HeadlessBtn
            value={getBtnName('FIND')}
            handleClick={handleChange}
            StyledBtn={BlackFitBtn}
          />
          <HeadlessBtn
            value={getBtnName('CANCEL')}
            handleClick={() => clearModal()}
            StyledBtn={PaperBlurFitBtn}
          />
        </BtnContainer>
      </ChangeModalComp>
    </FindModalFrame>
  );
};


export const FindModalFrame = styled.div<{ $isClosing: boolean }>`
  position: relative;
  padding: 20px;
  width: 440px;
  height: fit-content;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'scale(0.8)' : 'scale(1)')};

  transition: all 0.3s ease-in;
  ${paperTextureModal}
  background-color: ${({ theme }) => theme.colors.color_Alpha_05};
  ${popIn}
`;

const ChangeModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 2vh 1vw;
`

const Title = styled.h2`
  margin-bottom: 44px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 1vh;

  margin-top: 0.5vh;
`

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.color_alert_red};
  margin-bottom: 10px;
  min-height: 18px;
  ${({ theme }) => theme.typography.Body_04};
`;

export default FindPWModal;